"use client";

/**
 * DeskFeedModule — market/data feed as an instrument widget (not page subnav).
 * Uses WidgetChrome so it matches Equity / Risk module language.
 */
import React, { useEffect, useMemo, useState } from "react";
import type { DeskFeedStatus } from "./lib/types";
import { STALE_MS } from "./lib/deskFormat";
import { WidgetChrome } from "./WidgetChrome";
import styles from "./WidgetChrome.module.css";
import local from "./DeskFeedModule.module.css";

export interface DeskFeedModuleProps {
  status: DeskFeedStatus;
  updating?: boolean;
  className?: string;
}

export function DeskFeedModule({
  status,
  updating = false,
  className = "",
}: DeskFeedModuleProps) {
  const [now, setNow] = useState(() => Date.now());
  const staleMs = status.staleMs ?? STALE_MS;
  const ageMs = status.lastRefreshed
    ? now - status.lastRefreshed.getTime()
    : null;
  const delayed =
    status.realtime !== "connected" &&
    ageMs != null &&
    ageMs > staleMs &&
    status.http === "connected";

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  const presentation = useMemo(() => {
    if (updating || status.http === "connecting") {
      return { label: "Updating", detail: "Refreshing portfolio data", tone: "muted" };
    }
    if (status.http === "error" || status.realtime === "error") {
      return { label: "Data unavailable", detail: "Some figures may be incomplete", tone: "bad" };
    }
    if (status.http === "offline") {
      return { label: "Offline", detail: "Portfolio updates are paused", tone: "bad" };
    }
    if (delayed) {
      return { label: "Updates delayed", detail: "Showing the latest available figures", tone: "warn" };
    }
    if (status.lastRefreshed) {
      return { label: "Data current", detail: "Portfolio updates automatically", tone: "ok" };
    }
    return { label: "Loading data", detail: "Preparing your portfolio", tone: "muted" };
  }, [delayed, status.http, status.lastRefreshed, status.realtime, updating]);

  return (
    <WidgetChrome
      label="Portfolio data"
      meta="Portfolio data"
      aria-label="Portfolio data status"
      className={className}
    >
      <div className={local.status} role="status" aria-live="polite">
        <span
          className={`${local.statusMark} ${presentation.tone === "ok"
            ? local.ok
            : presentation.tone === "bad"
              ? local.bad
              : presentation.tone === "warn"
                ? local.warn
                : ""
            }`}
          aria-hidden
        />
        <span className={`${styles.mono} ${local.statusLabel}`}>
          {presentation.label}
        </span>
        <span className={local.statusDetail}>{presentation.detail}</span>
      </div>
    </WidgetChrome>
  );
}
